import { useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { wheelDeltaToPixels } from '#/shared/lib/hooks'
import { cn } from '#/shared/lib/utils'

import { filterGroups } from './slash-command'
import type { SlashCommandGroup, SlashCommandItem } from './slash-command'
import { useSlashNav } from './use-slash-nav'
import type { SlashKeyHandler } from './use-slash-command'

const MENU_WIDTH = 260
const LIST_MAX_HEIGHT = 360
const GAP = 6

/** A leaf field row — label on the left, sample value muted on the right. */
function ItemRow({
  item,
  active,
  activeRef,
  onHover,
  onSelect,
}: {
  item: SlashCommandItem
  active: boolean
  activeRef: React.RefObject<HTMLButtonElement | null>
  onHover: () => void
  onSelect: () => void
}) {
  // Keep the button interactive even when disabled so a click still
  // preventDefaults and the editor keeps focus — it just inserts nothing.
  const disabled = item.disabled ?? false
  return (
    <button
      ref={active ? activeRef : undefined}
      type="button"
      aria-disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onMouseEnter={disabled ? undefined : onHover}
      onClick={disabled ? undefined : onSelect}
      className={cn(
        'flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-sm',
        disabled && 'cursor-default text-gray-300',
        !disabled &&
          (active
            ? 'bg-gray-100 text-gray-900'
            : 'text-gray-900 hover:bg-gray-100'),
      )}
    >
      <span className="truncate">{item.label}</span>
      {item.hint && (
        <span
          className={cn(
            'max-w-[45%] shrink-0 truncate text-xs',
            disabled ? 'text-gray-300' : 'text-gray-400',
          )}
        >
          {item.hint}
        </span>
      )}
    </button>
  )
}

function SectionHeader({ label }: { label: string }) {
  return (
    <div className="px-2 py-0.5 text-2xs font-medium tracking-wide text-gray-400 uppercase select-none">
      {label}
    </div>
  )
}

interface FlatSection {
  key: string
  label: string
  items: SlashCommandItem[]
}

/** One header level: each subgroup becomes a section; a group's own direct
 *  items fall back to the group label. */
function toFlatSections(groups: SlashCommandGroup[]): FlatSection[] {
  return groups.flatMap((group) =>
    group.sections.map((section, index) => ({
      key: `${group.id}:${index}`,
      label: section.label ?? group.label,
      items: section.items,
    })),
  )
}

interface ListProps {
  groups: SlashCommandGroup[]
  activeIndex: number
  activeRef: React.RefObject<HTMLButtonElement | null>
  onHover: (index: number) => void
  onSelect: (item: SlashCommandItem) => void
}

/** The single, flat option list: one header per section, then its items. */
function SlashList({
  groups,
  activeIndex,
  activeRef,
  onHover,
  onSelect,
}: ListProps) {
  const sections = useMemo(() => toFlatSections(groups), [groups])
  let flat = -1
  return (
    <div
      data-slash-scroll
      style={{ maxHeight: LIST_MAX_HEIGHT }}
      className="flex flex-col overflow-y-auto overscroll-contain p-1"
    >
      {sections.map((section, si) => (
        // Separate sections with a top margin only between them, so the first
        // header keeps the same top padding as the rest.
        <div
          key={section.key}
          className={cn('flex flex-col', si > 0 && 'mt-1')}
        >
          <SectionHeader label={section.label} />
          {section.items.map((item) => {
            flat += 1
            const index = flat
            return (
              <ItemRow
                key={item.id}
                item={item}
                active={index === activeIndex}
                activeRef={activeRef}
                onHover={() => onHover(index)}
                onSelect={() => onSelect(item)}
              />
            )
          })}
        </div>
      ))}
    </div>
  )
}

/** Keyboard-shortcut legend pinned to the bottom of the menu. */
function SlashFooterRow() {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-3.5 border-t border-gray-200 px-3 py-2 text-xs whitespace-nowrap text-gray-400 select-none">
      <span>{t('editor.slash.navigateHint')}</span>
      <span>{t('editor.slash.insertHint')}</span>
      <span>{t('editor.slash.closeHint')}</span>
    </div>
  )
}

function useOutsideDismiss(
  rootRef: React.RefObject<HTMLElement | null>,
  onDismiss: () => void,
) {
  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) onDismiss()
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [rootRef, onDismiss])
}

/**
 * Scroll the option list from a wheel anywhere over the menu (rows, headers,
 * footer or padding) — not just over the list itself. The menu is portaled
 * outside the Radix `Dialog`, whose `react-remove-scroll` `preventDefault`s
 * wheel events on out-of-tree content, killing native scroll; we listen in the
 * capture phase on the menu root and drive `scrollTop` ourselves so the lock
 * can never swallow it.
 *
 * The scroll node is resolved from the live DOM (`[data-slash-scroll]`) at
 * event time rather than a React ref: the list mounts only once there are
 * results, so while the query is still resolving (or empty) a ref would be
 * stale/null and the handler would silently no-op — invisible outside a modal
 * where native scroll still works, but fatal inside one where it's all we have.
 */
function useMenuWheelScroll(rootRef: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const onWheel = (event: WheelEvent) => {
      const list = root.querySelector<HTMLElement>('[data-slash-scroll]')
      if (!list || list.scrollHeight <= list.clientHeight) return
      list.scrollTop += wheelDeltaToPixels(event, list)
      event.preventDefault()
      event.stopPropagation()
    }
    root.addEventListener('wheel', onWheel, { passive: false, capture: true })
    return () => root.removeEventListener('wheel', onWheel, { capture: true })
  }, [rootRef])
}

function EmptyRow() {
  const { t } = useTranslation()
  return (
    <div className="px-3 py-3 text-center text-sm text-gray-400 select-none">
      {t('editor.slash.noResults')}
    </div>
  )
}

interface SlashCommandMenuProps {
  groups: SlashCommandGroup[]
  query: string
  rect: { bottom: number; left: number } | null
  keyRef: React.RefObject<SlashKeyHandler>
  onSelect: (item: SlashCommandItem) => void
  onDismiss: () => void
}

/** Notion-style `/` menu: a single grouped option list (filtered by the text
 *  typed after `/`) with a shortcut footer. Focus stays in the editor; keys
 *  route in through `keyRef`. */
export function SlashCommandMenu({
  groups,
  query,
  rect,
  keyRef,
  onSelect,
  onDismiss,
}: SlashCommandMenuProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const activeRef = useRef<HTMLButtonElement>(null)
  const filtered = useMemo(() => filterGroups(groups, query), [groups, query])
  const nav = useSlashNav({
    groups: filtered,
    query,
    keyRef,
    onSelect,
    onDismiss,
  })
  useOutsideDismiss(rootRef, onDismiss)
  useMenuWheelScroll(rootRef)

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' })
  }, [nav.activeIndex])

  const top = (rect?.bottom ?? GAP) + GAP
  const rawLeft = rect?.left ?? GAP
  const left = Math.max(
    GAP,
    Math.min(rawLeft, window.innerWidth - MENU_WIDTH - GAP),
  )

  return createPortal(
    <div
      ref={rootRef}
      style={{ position: 'fixed', top, left, width: MENU_WIDTH }}
      // pointer-events-auto: the menu portals into <body>, and an open Radix
      // modal sets body pointer-events to none — without the opt-in the menu
      // ignores the mouse entirely (no wheel, hover or click) inside dialogs.
      className="pointer-events-auto z-[80] flex flex-col rounded-md border border-gray-200 bg-white shadow-popover"
    >
      {filtered.length === 0 ? (
        <EmptyRow />
      ) : (
        <SlashList
          groups={filtered}
          activeIndex={nav.activeIndex}
          activeRef={activeRef}
          onHover={nav.setActive}
          onSelect={onSelect}
        />
      )}
      <SlashFooterRow />
    </div>,
    document.body,
  )
}
